/**
 * Primer Deck c5: el mazo prototipo en uso.
 *
 * Es un **catalogo descriptivo**, no una restriccion de construccion. Sus 48
 * cartas y su reparto 12 / 20 / 16 describen **este** mazo; las reglas solo
 * imponen los maximos de 48 cartas y 12 Ofrendas (seccion 5 de `GAME_RULES.md`).
 *
 * `effectText` es **opaco**: se transcribe tal cual y nadie lo interpreta. Aqui
 * no hay DSL, AST, motor de efectos, disparos, objetivos, pila ni scripting.
 *
 * Los `definitionId` son **slugs provisionales** derivados del nombre. La
 * convencion de formato sigue pendiente (12.29): cuando se decida, se sustituyen
 * aqui y el resto del paquete no se entera.
 */
import { UNRESOLVED_CARD_CLASS, } from './cards.js';
import { createCardCatalog } from './catalog.js';
import { createCardCost } from './amounts.js';
import { definitionId } from './ids.js';
/** Las 12 Ofrendas del mazo, en 2 definiciones. */
const OFFERS = [
    {
        type: 'offer',
        definitionId: definitionId('ofrenda-de-hueso'),
        name: 'Ofrenda de Hueso',
        description: 'La anciana dejó el fémur sobre el altar. Al amanecer, la tumba de su hijo estaba vacía.',
        cardClass: UNRESOLVED_CARD_CLASS,
        value: 1,
    },
    {
        type: 'offer',
        definitionId: definitionId('ofrenda-de-sangre'),
        name: 'Ofrenda de Sangre',
        description: 'Pidieron una gota. El cáliz siguió bebiendo después de que ella retiró la mano.',
        cardClass: UNRESOLVED_CARD_CLASS,
        value: 2,
        effectText: 'Cuando agotas esta Ofrenda para generar recurso, pierdes 1 PV.',
    },
];
/** Las 20 Invocaciones del mazo, en 12 definiciones. */
const INVOCATIONS = [
    {
        type: 'invocation',
        definitionId: definitionId('restos-inquietos'),
        name: 'Restos Inquietos',
        description: 'Los enterraron sin nombre junto al camino. Cada noche avanzan unos pasos hacia casa.',
        cardClass: 'undead',
        cost: createCardCost(0),
        power: 0,
        life: 1,
        range: 1,
        penetration: false,
        effectText: 'Cuando esta Invocación sea destruida durante el turno rival, roba 1 carta.',
    },
    {
        type: 'invocation',
        definitionId: definitionId('zombi'),
        name: 'Zombi',
        description: 'Su esposa reconoció el abrigo antes que el rostro. No abrió la puerta.',
        cardClass: 'undead',
        cost: createCardCost(1),
        power: 1,
        life: 1,
        range: 1,
        penetration: false,
    },
    {
        type: 'invocation',
        definitionId: definitionId('necrofago'),
        name: 'Necrófago',
        description: 'Primero siguió al ejército por los cadáveres. Después aprendió que los vivos marchaban más despacio.',
        cardClass: 'beast',
        cost: createCardCost(1),
        power: 1,
        life: 2,
        range: 1,
        penetration: false,
        effectText: 'Una vez durante tu turno, puedes sacrificar un Zombi que controles. Si lo haces, esta Invocación obtiene +2 Poder hasta el final del turno.',
    },
    {
        type: 'invocation',
        definitionId: definitionId('sacerdotisa-del-osario'),
        name: 'Sacerdotisa del Osario',
        description: 'Alzó las manos y los muertos enderezaron la espalda. Algunos aún recordaban aquella voz.',
        cardClass: 'undead',
        cost: createCardCost(1),
        power: 0,
        life: 1,
        range: 1,
        penetration: false,
        effectText: 'Los No Muertos adyacentes a esta Invocación en su misma línea obtienen +1 Poder y +1 Alcance.',
    },
    {
        type: 'invocation',
        definitionId: definitionId('lanzahuesos'),
        name: 'Lanzahuesos',
        description: 'Cuando se terminaron las flechas, el capitán miró el cementerio detrás de las murallas.',
        cardClass: 'undead',
        cost: createCardCost(2),
        power: 2,
        life: 2,
        range: 2,
        penetration: false,
    },
    {
        type: 'invocation',
        definitionId: definitionId('abominacion-cosida'),
        name: 'Abominación Cosida',
        description: 'El cirujano necesitó siete cuerpos para terminarla. A la mañana siguiente faltaban ocho.',
        cardClass: 'beast',
        cost: createCardCost(2),
        power: 2,
        life: 3,
        range: 1,
        penetration: true,
    },
    {
        type: 'invocation',
        definitionId: definitionId('jinete-sin-cabeza'),
        name: 'Jinete sin Cabeza',
        description: 'El caballo regresó tres noches después de la batalla. El jinete seguía montado. Su cabeza no.',
        cardClass: 'undead',
        cost: createCardCost(2),
        power: 3,
        life: 2,
        range: 2,
        penetration: false,
        effectText: 'Corte amplio. Cuando esta Invocación inflige daño a una Invocación, inflige 1 de daño a las Invocaciones adyacentes a ella en la misma línea.',
    },
    {
        type: 'invocation',
        definitionId: definitionId('arquero-funerario'),
        name: 'Arquero Funerario',
        description: 'Lo sepultaron con su arco. Nadie pensó en quitarle las flechas.',
        cardClass: 'undead',
        cost: createCardCost(3),
        power: 3,
        life: 2,
        range: 2,
        penetration: false,
    },
    {
        type: 'invocation',
        definitionId: definitionId('vampiro-de-montparnasse'),
        name: 'Vampiro de Montparnasse',
        description: 'Durante cuarenta años dejó una rosa en la misma tumba. Una noche, la tumba se la devolvió.',
        cardClass: 'undead',
        cost: createCardCost(3),
        power: 3,
        life: 3,
        range: 2,
        penetration: false,
    },
    {
        type: 'invocation',
        definitionId: definitionId('gigante-putrefacto'),
        name: 'Gigante Putrefacto',
        description: 'La aldea oyó sus pasos durante dos días antes de verlo. Para entonces ya era tarde para huir.',
        cardClass: 'beast',
        cost: createCardCost(4),
        power: 4,
        life: 5,
        range: 2,
        penetration: true,
    },
    {
        type: 'invocation',
        definitionId: definitionId('heraldo-de-la-fosa'),
        name: 'Heraldo de la Fosa',
        description: 'Llegó solo a las puertas y anunció que el ejército venía detrás. Mentía: ya estaba debajo.',
        cardClass: 'undead',
        cost: createCardCost(5),
        power: 2,
        life: 5,
        range: 2,
        penetration: false,
        effectText: 'Esta Invocación no recibe daño de las respuestas de Invocaciones defensoras.',
    },
    {
        type: 'invocation',
        definitionId: definitionId('nigromante'),
        name: 'Nigromante',
        description: 'Pasó treinta años aprendiendo a llamar a los muertos. La última noche descubrió que ellos también sabían su nombre.',
        cardClass: 'undead',
        cost: createCardCost(6),
        power: 6,
        life: 7,
        range: 3,
        penetration: true,
    },
];
/** Los 16 Hechizos del mazo, en 12 definiciones. */
const SPELLS = [
    {
        type: 'spell',
        definitionId: definitionId('pacto-de-ceniza'),
        name: 'Pacto de Ceniza',
        description: 'Quemó la carta sin leer la última línea. Las palabras aparecieron después sobre su piel.',
        cardClass: 'immediate',
        cost: createCardCost(0),
        effectText: 'Pierdes 1 PV. Roba 1 carta.',
    },
    {
        type: 'spell',
        definitionId: definitionId('toque-de-podredumbre'),
        name: 'Toque de Podredumbre',
        description: 'El verdugo le rozó el hombro al condenado. Murió tres días antes de que cayera el hacha.',
        cardClass: 'immediate',
        cost: createCardCost(0),
        effectText: 'Inflige 1 de daño a una Invocación.',
    },
    {
        type: 'spell',
        definitionId: definitionId('ojo-del-sepulcro'),
        name: 'Ojo del Sepulcro',
        description: 'El sepulturero tapó el agujero con tierra. Desde entonces algo observa por él.',
        cardClass: 'immediate',
        cost: createCardCost(1),
        effectText: 'Una Invocación obtiene +1 Alcance hasta el final del turno.',
    },
    {
        type: 'spell',
        definitionId: definitionId('disipacion'),
        name: 'Disipación',
        description: 'El exorcista terminó la última palabra. Durante un instante, la casa volvió a pertenecer a los vivos.',
        cardClass: 'immediate',
        cost: createCardCost(2),
        effectText: 'Envía un Hechizo Permanente del Campo al descarte de su propietario.',
    },
    {
        type: 'spell',
        definitionId: definitionId('estandarte-profano'),
        name: 'Estandarte Profano',
        description: 'Nadie recuerda quién lo levantó primero. Todos recuerdan lo que hicieron después de verlo.',
        cardClass: 'permanent',
        cost: createCardCost(2),
        effectText: 'Tus Invocaciones en esta línea obtienen +1 Poder.',
    },
    {
        type: 'spell',
        definitionId: definitionId('atalaya-funeraria'),
        name: 'Atalaya Funeraria',
        description: 'El vigía murió en su puesto durante el invierno. En primavera todavía señalaba hacia el horizonte.',
        cardClass: 'permanent',
        cost: createCardCost(2),
        effectText: 'Tus Invocaciones en esta línea obtienen +1 Alcance.',
    },
    {
        type: 'spell',
        definitionId: definitionId('niebla-sepulcral'),
        name: 'Niebla Sepulcral',
        description: 'Los soldados entraron juntos en la niebla. Cada uno salió convencido de haber marchado solo.',
        cardClass: 'permanent',
        subtype: 'climate',
        cost: createCardCost(2),
        effectText: 'Las Invocaciones no pueden cambiar de línea una vez jugadas.',
    },
    {
        type: 'spell',
        definitionId: definitionId('baluarte-inmortal'),
        name: 'Baluarte Inmortal',
        description: 'La fortaleza cayó hace tres siglos. La puerta todavía se niega a aceptarlo.',
        cardClass: 'permanent',
        cost: createCardCost(3),
        effectText: 'Protege a su controlador de ataques directos. Puede ser atacado como excepción a la regla general de Hechizos. Un ataque de Poder 4 o más lo destruye; ataques de Poder 3 o menos no lo destruyen. No ataca ni responde.',
    },
    {
        type: 'spell',
        definitionId: definitionId('maldicion-de-la-fosa'),
        name: 'Maldición de la Fosa',
        description: 'Abrieron una tumba para esconderse del bombardeo. Algo dentro les hizo espacio.',
        cardClass: 'immediate',
        cost: createCardCost(4),
        effectText: 'Inflige 2 de daño a todas las Invocaciones de una línea enemiga elegida.',
    },
    {
        type: 'spell',
        definitionId: definitionId('campanas-del-fin'),
        name: 'Campanas del Fin',
        description: 'La primera campanada despertó a los muertos. La sexta hizo callar a los vivos.',
        cardClass: 'immediate',
        cost: createCardCost(6),
        effectText: 'Inflige 3 de daño a todas las Invocaciones de una línea enemiga elegida.',
    },
    {
        type: 'spell',
        definitionId: definitionId('juicio'),
        name: 'Juicio',
        description: 'El condenado pidió una última palabra. El juez respondió borrando su nombre del registro.',
        cardClass: 'immediate',
        cost: createCardCost(8),
        effectText: 'Envía una Invocación al descarte de su propietario.',
    },
    {
        type: 'spell',
        definitionId: definitionId('ultimo-rito'),
        name: 'Último Rito',
        description: 'El sacerdote cerró los ojos de sus hombres uno por uno. Cuando terminó, los suyos volvieron a abrirse.',
        cardClass: 'immediate',
        cost: createCardCost(6),
        effectText: 'Sacrifica cualquier cantidad de Invocaciones que controles. Recuperas 1 PV por cada Invocación sacrificada.',
    },
];
/** Las 26 definiciones unicas del Primer Deck c5. */
export const PRIMER_DECK_C5_DEFINITIONS = [
    ...OFFERS,
    ...INVOCATIONS,
    ...SPELLS,
];
/** Catalogo autoritativo del Primer Deck c5. */
export const PRIMER_DECK_C5_CATALOG = createCardCatalog(PRIMER_DECK_C5_DEFINITIONS);
/** Lista de construccion: 48 cartas a partir de esas 26 definiciones. */
export const PRIMER_DECK_C5_LIST = {
    entries: [
        { definitionId: definitionId('ofrenda-de-hueso'), quantity: 10 },
        { definitionId: definitionId('ofrenda-de-sangre'), quantity: 2 },
        { definitionId: definitionId('restos-inquietos'), quantity: 4 },
        { definitionId: definitionId('zombi'), quantity: 4 },
        { definitionId: definitionId('necrofago'), quantity: 1 },
        { definitionId: definitionId('sacerdotisa-del-osario'), quantity: 1 },
        { definitionId: definitionId('lanzahuesos'), quantity: 2 },
        { definitionId: definitionId('abominacion-cosida'), quantity: 1 },
        { definitionId: definitionId('jinete-sin-cabeza'), quantity: 1 },
        { definitionId: definitionId('arquero-funerario'), quantity: 2 },
        { definitionId: definitionId('vampiro-de-montparnasse'), quantity: 1 },
        { definitionId: definitionId('gigante-putrefacto'), quantity: 1 },
        { definitionId: definitionId('heraldo-de-la-fosa'), quantity: 1 },
        { definitionId: definitionId('nigromante'), quantity: 1 },
        { definitionId: definitionId('pacto-de-ceniza'), quantity: 2 },
        { definitionId: definitionId('toque-de-podredumbre'), quantity: 2 },
        { definitionId: definitionId('ojo-del-sepulcro'), quantity: 2 },
        { definitionId: definitionId('disipacion'), quantity: 2 },
        { definitionId: definitionId('estandarte-profano'), quantity: 1 },
        { definitionId: definitionId('atalaya-funeraria'), quantity: 1 },
        { definitionId: definitionId('niebla-sepulcral'), quantity: 1 },
        { definitionId: definitionId('baluarte-inmortal'), quantity: 1 },
        { definitionId: definitionId('maldicion-de-la-fosa'), quantity: 1 },
        { definitionId: definitionId('campanas-del-fin'), quantity: 1 },
        { definitionId: definitionId('juicio'), quantity: 1 },
        { definitionId: definitionId('ultimo-rito'), quantity: 1 },
    ],
};
//# sourceMappingURL=primerDeckC5.js.map